import { Clock, AlertCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCompanyPlans } from '@/hooks/useCompanyPlans';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TrialBannerProps {
  companyId: string;
  companySlug: string;
}

export function TrialBanner({ companyId, companySlug }: TrialBannerProps) {
  const navigate = useNavigate();
  const { data: plans = [], isLoading } = useCompanyPlans(companyId);

  if (isLoading) return null;

  // Filter active trials
  const activeTrials = plans.filter(
    plan => plan.is_trial && 
           plan.status === 'active' && 
           plan.trial_expires_at
  );

  if (activeTrials.length === 0) return null;

  // Get the trial that expires soonest
  const nearestExpiry = activeTrials.reduce((earliest, plan) => {
    const expiryDate = new Date(plan.trial_expires_at!);
    const earliestDate = new Date(earliest.trial_expires_at!);
    return expiryDate < earliestDate ? plan : earliest;
  });

  // Calculate days remaining
  const expiryDate = new Date(nearestExpiry.trial_expires_at!);
  const today = new Date();
  const diffTime = expiryDate.getTime() - today.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Determine banner style and message based on days remaining
  const getBannerStyles = () => {
    if (daysRemaining <= 0) {
      return {
        className: 'bg-destructive/10 text-destructive border-l-4 border-destructive',
        icon: AlertCircle,
        message: 'Seu período de trial expirou.',
        submessage: 'Entre em contato para reativar o acesso aos sistemas.',
        ctaText: 'Assinar Agora',
        ctaVariant: 'destructive' as const
      };
    }
    if (daysRemaining <= 3) {
      return {
        className: 'bg-destructive/10 text-destructive border-l-4 border-destructive',
        icon: AlertCircle,
        message: `Atenção! Seu trial expira em ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'}.`,
        submessage: 'Contrate agora para não perder o acesso aos sistemas.',
        ctaText: 'Assinar Agora',
        ctaVariant: 'destructive' as const
      };
    }
    if (daysRemaining <= 7) {
      return {
        className: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-l-4 border-orange-500',
        icon: Clock,
        message: `Seu trial expira em ${daysRemaining} dias.`,
        submessage: 'Entre em contato para continuar usando o sistema após o período de avaliação.',
        ctaText: 'Ver Planos',
        ctaVariant: 'default' as const
      };
    }
    return {
      className: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-l-4 border-blue-500',
      icon: Sparkles,
      message: `Você está no período de trial! Aproveite todas as funcionalidades por mais ${daysRemaining} dias.`,
      submessage: activeTrials.length > 1 ? `Você tem ${activeTrials.length} sistemas em período trial.` : '',
      ctaText: 'Ver Planos',
      ctaVariant: 'default' as const
    };
  };

  const bannerConfig = getBannerStyles();
  const Icon = bannerConfig.icon;

  return (
    <div className={cn(
      'w-full px-4 sm:px-6 lg:px-8 py-4 mb-6',
      bannerConfig.className
    )}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 w-full">
        <div className="flex items-start gap-3">
          <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-semibold text-sm sm:text-base">
              {bannerConfig.message}
            </p>
            {bannerConfig.submessage && (
              <p className="text-xs sm:text-sm opacity-90">
                {bannerConfig.submessage}
              </p>
            )}
          </div>
        </div>
        <Button 
          variant={bannerConfig.ctaVariant}
          size="sm"
          className="flex-shrink-0"
          onClick={() => {
            navigate(`/company/${companySlug}/admin/plans`);
          }}
        >
          {bannerConfig.ctaText}
        </Button>
      </div>
    </div>
  );
}
